import type { ButtonHTMLAttributes, ReactNode } from "react";

import { cn } from "@/lib/utils";

interface HwButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** `primary` is the solid blue action, `secondary` the grey one beside it. */
  variant?: "primary" | "secondary";
  children: ReactNode;
}

/**
 * The BOI site's content button: DM Sans 1rem/1.2rem, weight 500, 4px corners.
 * Exported so a router `Link` can wear the same primary look.
 */
export const HW_BUTTON_BASE =
  "inline-flex cursor-pointer items-center justify-center gap-2 rounded-[4px] border-none text-[1rem] leading-[1.2rem] font-medium tracking-[0.1px] transition-colors duration-300 focus-visible:outline-hw-blue focus-visible:outline-2 focus-visible:outline-offset-[3px] disabled:cursor-default disabled:opacity-40";

export const HW_BUTTON_PRIMARY =
  "bg-hw-blue text-hw-paper hover:bg-hw-blue-deep focus-visible:bg-hw-blue-deep disabled:hover:bg-hw-blue px-7 py-3";

const HW_BUTTON_SECONDARY =
  "bg-hw-secondary text-hw-ink hover:text-hw-blue focus-visible:text-hw-blue disabled:hover:text-hw-ink px-[18px] py-3";

/** The two button shapes the diagnostic uses, kept identical across screens. */
export function HwButton({ variant = "primary", className, children, ...props }: HwButtonProps) {
  return (
    <button
      type="button"
      {...props}
      className={cn(
        HW_BUTTON_BASE,
        variant === "primary" ? HW_BUTTON_PRIMARY : HW_BUTTON_SECONDARY,
        className,
      )}
    >
      {children}
    </button>
  );
}
